import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowRight, CheckCircle, Trophy, TrendingUp, Target, Award } from 'lucide-react';
import { Button } from '../ui/button';
import { resultsData, galleryImages } from '../../data/mock';

const FadeInSection = ({ children, className = '', delay = 0 }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const placeholderColors = [
  'from-[rgba(243,112,33,0.15)] to-[rgba(243,112,33,0.06)]',
  'from-[rgba(0,180,216,0.15)] to-[rgba(0,180,216,0.06)]',
  'from-[rgba(243,112,33,0.1)] to-[rgba(0,180,216,0.1)]',
  'from-[rgba(0,180,216,0.1)] to-[rgba(218,255,1,0.08)]',
];

/* ===================== RESULTS & TRUST ===================== */
export const ResultsTrustSection = () => (
  <section className="py-24 bg-[rgb(26,28,30)] relative overflow-hidden">
    <div className="absolute top-0 left-1/3 w-96 h-96 bg-[rgba(243,112,33,0.04)] rounded-full blur-[120px]" />

    <div className="max-w-[1200px] mx-auto px-6 relative">
      <FadeInSection>
        <div className="text-center mb-16">
          <span className="text-[#F37021] text-sm font-semibold tracking-widest uppercase">
            Results & Impact
          </span>

          <h2 className="text-4xl md:text-5xl font-bold text-white mt-3 mb-4">
            Measurable Academic Outcomes
          </h2>

          <p className="text-[rgb(161,161,170)] text-lg max-w-2xl mx-auto">
            Consistent results across law, management, and integrated academic programs.
          </p>
        </div>
      </FadeInSection>

      {/* Metrics */}
      <FadeInSection delay={0.1}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {resultsData.metrics.map((m, i) => (
            <div
              key={i}
              className="bg-[rgb(17,17,19)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-8 text-center 
                         hover:-translate-y-1 hover:border-[rgba(243,112,33,0.35)] transition-all duration-300"
            >
              <div className="text-3xl md:text-4xl font-bold text-[#F37021] mb-2">
                {m.value}
              </div>
              <div className="text-[rgb(161,161,170)] text-sm">
                {m.label}
              </div>
            </div>
          ))}
        </div>
      </FadeInSection>

      <div className="grid md:grid-cols-2 gap-8">

        {/* Exams */}
        <FadeInSection delay={0.2}>
          <div className="bg-[rgb(17,17,19)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-[rgba(243,112,33,0.1)] flex items-center justify-center">
                <Target size={20} className="text-[#F37021]" />
              </div>
              <h3 className="text-xl font-semibold text-white">
                Competitive Exams Covered
              </h3>
            </div>

            <div className="flex flex-wrap gap-3">
              {resultsData.examsCovered.map((exam, i) => (
                <span
                  key={i}
                  className="px-4 py-2 rounded-xl bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)]
                             text-[rgb(218,218,218)] text-sm font-medium
                             hover:border-[#F37021] hover:text-[#F37021] transition-colors"
                >
                  {exam}
                </span>
              ))}
            </div>
          </div>
        </FadeInSection>

        {/* Achievements */}
        <FadeInSection delay={0.3}>
          <div className="bg-[rgb(17,17,19)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-[rgba(243,112,33,0.1)] flex items-center justify-center">
                <Trophy size={20} className="text-[#F37021]" />
              </div>
              <h3 className="text-xl font-semibold text-white">
                Academic Milestones
              </h3>
            </div>

            <div className="space-y-4">
              {resultsData.achievements.map((a, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle
                    size={16}
                    className="text-[#F37021] mt-0.5 flex-shrink-0"
                  />
                  <span className="text-[rgb(218,218,218)] text-sm leading-relaxed">
                    {a}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </FadeInSection>

      </div>
    </div>
  </section>
);


/* ===================== GALLERY PREVIEW ===================== */
export const GalleryPreview = () => {
  const previewImages = galleryImages.slice(0, 4);

  return (
    <section className="py-24 bg-[rgb(26,28,30)] relative">
      <div className="max-w-[1200px] mx-auto px-6">
        <FadeInSection>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <span className="text-[#F37021] text-sm font-semibold tracking-widest uppercase">
                Gallery
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-white mt-3">
                Life at Scale Up Academy
              </h2>
            </div>

            <Link to="/gallery" className="mt-4 md:mt-0">
              <Button
                variant="outline"
                className="border-2 border-[rgb(63,63,63)] text-white 
                           hover:border-[#F37021] hover:text-[#F37021] 
                           hover:bg-[rgba(243,112,33,0.06)]
                           rounded-xl px-6 h-11 text-sm bg-transparent
                           transition-all duration-200 hover:-translate-y-0.5"
              >
                View Full Gallery <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </div>
        </FadeInSection>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {previewImages.map((img, i) => (
            <FadeInSection key={img.id} delay={i * 0.1}>
              <Link to="/gallery" className="group block">
                <div className="relative aspect-square rounded-2xl overflow-hidden 
                                border border-[rgba(255,255,255,0.1)]
                                group-hover:border-[rgba(243,112,33,0.35)]
                                transition-all duration-300 group-hover:-translate-y-1">

                  {/* Image */}
                  <img
                    src={img.image}
                    alt={img.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />

                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent opacity-90" />

                  {/* Text */}
                  <div className="absolute bottom-4 left-4 right-4">
                    <span className="inline-block mb-1 px-3 py-1 rounded-lg
                                     bg-[rgba(243,112,33,0.15)]
                                     text-[#F37021] text-xs font-semibold">
                      {img.category}
                    </span>
                    <h4 className="text-white font-medium text-sm leading-snug">
                      {img.title}
                    </h4>
                  </div>
                </div>
              </Link>
            </FadeInSection>
          ))}
        </div>
      </div>
    </section>
  );
};


/* ===================== FINAL CTA ===================== */
export const FinalCTA = () => (
  <section className="py-24 bg-[rgb(17,17,19)] relative">
    <div className="max-w-[1200px] mx-auto px-6">
      <FadeInSection>
        <div className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-3xl p-12 md:p-16 text-center relative overflow-hidden">
          <div className="absolute top-0 left-1/4 w-64 h-64 bg-[rgba(243,112,33,0.06)] rounded-full blur-[80px]" />
          <div className="absolute bottom-0 right-1/4 w-48 h-48 bg-[rgba(0,180,216,0.06)] rounded-full blur-[60px]" />
          <div className="relative">
            <div className="w-16 h-16 rounded-2xl bg-[rgba(243,112,33,0.1)] flex items-center justify-center mx-auto mb-6">
              <Award size={30} className="text-[#F37021]" />
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-5">
              Ready to <span className="text-[#F37021]">Scale Up</span> Your Career?
            </h2>
            <p className="text-[rgb(161,161,170)] text-lg max-w-xl mx-auto mb-10">
              Join thousands of successful students. Book your free demo class today — no obligations, just a head start.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button className="bg-[#F37021] hover:bg-[#DC5A14] text-white font-semibold rounded-xl px-10 h-14 text-base border-0 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_25px_rgba(243,112,33,0.35)]">
                  Book Free Demo Class <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
              <a href="tel:+919876543210">
                <Button variant="outline" className="border-2 border-[rgb(63,63,63)] text-white hover:border-[#F37021] hover:text-[#F37021] hover:bg-[rgba(243,112,33,0.06)] rounded-xl px-10 h-14 text-base bg-transparent transition-all duration-200 hover:-translate-y-0.5">
                  Talk to a Counsellor
                </Button>
              </a>
            </div>
          </div>
        </div>
      </FadeInSection>
    </div>
  </section>
);
